import * as THREE from 'three';
import {
  edgeStyleForNest,
  edgeStyleForReturn,
  edgeStyleFromSource,
} from '../../utils/neuralSceneUtils';
import { curveForKind, nestDepthDim } from '../neuralMaterials';
import {
  createFilamentLine,
  filamentEndsMoved,
  filamentLod,
  rememberFilamentEnds,
  setFilamentResolution,
  shouldRewriteFilament,
  writeFilamentCurve,
  writeFilamentGradient,
} from '../filamentLines';

/**
 * Style for a wire given its kind and endpoints. Wires on a nested layer recede
 * with it; stems / returns that bridge layers dim to the geometric mean.
 * @param {'dep'|'nest'|'return'} kind
 */
export function edgeStyle(kind, src, tgt) {
  if (kind === 'nest') {
    const s = edgeStyleForNest(src);
    return { ...s, opacity: s.opacity * Math.sqrt(nestDepthDim(tgt)) };
  }
  if (kind === 'return') {
    const s = edgeStyleForReturn(src);
    return { ...s, opacity: s.opacity * Math.sqrt(nestDepthDim(src)) };
  }
  const s = edgeStyleFromSource(src || { statusCategory: 'not_started' });
  return { ...s, opacity: s.opacity * nestDepthDim(tgt) };
}

export function createEdgeController({
  nodeGroup,
  meshByTaskId,
  edgeLinesByKey,
  lineResolution,
}) {
  const WHITE = new THREE.Color(0xffffff);

  function reconcileEdges(graph) {
    const { nodes, edges } = graph;
    const nextKeys = new Set();

    edges.forEach(([ia, ib, rawKind]) => {
      const na = nodes[ia];
      const nb = nodes[ib];
      if (!na?.id || !nb?.id) return;
      const key = `${na.id}>${nb.id}`;
      nextKeys.add(key);
      const kind = rawKind || 'dep';

      let line = edgeLinesByKey.get(key);
      if (!line) {
        const { color } = edgeStyle(kind, na, nb);
        line = createFilamentLine(color);
        setFilamentResolution(line, lineResolution.w, lineResolution.h);
        line.userData = {
          ...line.userData,
          fromId: na.id,
          toId: nb.id,
          sourceNodeMeta: na,
          targetNodeMeta: nb,
          kind,
          exiting: false,
          targetOpacity: 0,
          styleDirty: true,
        };
        nodeGroup.add(line);
        edgeLinesByKey.set(key, line);
      } else {
        line.userData.exiting = false;
        line.userData.sourceNodeMeta = na;
        line.userData.targetNodeMeta = nb;
        if (line.userData.kind !== kind) {
          line.userData.kind = kind;
          line.userData.curve = null; // kind picks the curve shape
        }
        line.userData.styleDirty = true;
      }
      line.userData.targetOpacity = edgeStyle(kind, na, nb).opacity;
    });

    edgeLinesByKey.forEach((lineObj, key) => {
      if (!nextKeys.has(key)) {
        lineObj.userData.exiting = true;
        lineObj.userData.targetOpacity = 0;
      }
    });
  }

  function refreshOccluders() {}
  function markOccludersDirty() {}
  function finishOccluderClip() {}

  function updateEdgeCurveGeometry(lineObj, va, vb, sourceMeta, camera, forceHigh = false) {
    const ud = lineObj.userData;
    const endsMoved = filamentEndsMoved(ud, va, vb);
    if (!endsMoved && !forceHigh && !ud.styleDirty) {
      ud.lodQuiet = (ud.lodQuiet || 0) + 1;
      if (ud.lodQuiet % 3 !== 1 && ud.sampleCount) return;
    } else {
      ud.lodQuiet = 0;
    }
    if (endsMoved || !ud.curve) {
      ud.curve = curveForKind(ud.kind, va, vb, ud.curve);
    }
    const lod = filamentLod(camera, va, vb, ud.curve, forceHigh);
    ud.lodBand = lod.band;
    const nestBonus = (ud.kind === 'nest' || ud.kind === 'return') && lod.band >= 3 ? 4 : 0;
    const samples = lod.samples + nestBonus;
    if (endsMoved || shouldRewriteFilament(ud, va, vb, samples, forceHigh)) {
      writeFilamentCurve(lineObj, ud.curve, samples, va, vb);
      rememberFilamentEnds(ud, va, vb);
    }
    if (ud.styleDirty) {
      const src = sourceMeta || { statusCategory: 'not_started' };
      const tgt = ud.targetNodeMeta;
      const { color, opacity } = edgeStyle(ud.kind, src, tgt);
      writeFilamentGradient(lineObj, color, color);
      if (!ud.exiting) ud.targetOpacity = opacity;
      ud.styleDirty = false;
    }
    lineObj.material.color.copy(WHITE);
    if (ud.glow) ud.glow.material.color.copy(WHITE);
  }

  function setLineResolution(width, height) {
    lineResolution.w = width;
    lineResolution.h = height;
    edgeLinesByKey.forEach((line) => setFilamentResolution(line, width, height));
  }

  return {
    reconcileEdges,
    refreshOccluders,
    markOccludersDirty,
    finishOccluderClip,
    updateEdgeCurveGeometry,
    setLineResolution,
  };
}
